import React, { VFC, useState, useCallback } from 'react';
import { makeStyles, Button } from '@dxb-web-ui-framework/design-system';
import { useNavigate } from 'react-router-dom';
import { TFunction } from 'react-i18next';
import { TransferServiceImpl } from '../../../fundTransfer/src/domain/Transfer/usecases';
import { ClientSideWizardFlowPresenter } from '../../../content-composer/src/components/ClientSideWizard/ClientSideWizardFlowPresenter';
import { GenericDialog } from '../../../content-composer/src/components/Generic/GenericDialog/GenericDialog';
import { ConsolidatedTransactionDetails } from '../../../transaction/src/domain/Transaction/entities/ConsolidatedTransaction';
import { getRoutePath } from '../../../content-composer/src/components/Routes/routePaths';
import { useSelectedCustomerId } from '../../../userProfile/src/contexts/UserInfo/hooks';
import { SuccessAcceptedDealerRate, InitiateRequestDealerInApp2FA } from './steps';

export interface FinalizeProps {
  t: TFunction;
  transactionDetails: ConsolidatedTransactionDetails;
  fundTransfersService: TransferServiceImpl;
  onRefetchTransactionDetails: Function;
}

const useStyles = makeStyles(theme => ({
  dialogContentClassName: {
    paddingTop: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(4),
    },
  },
}));

const Finalize: VFC<FinalizeProps> = ({ t, transactionDetails, fundTransfersService, onRefetchTransactionDetails }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<'initTransfer' | 'makeTransfer'>('initTransfer');
  const navigate = useNavigate();
  const selectedCustomerId = useSelectedCustomerId();

  const onToggleDealerRateAcceptanceDialog = useCallback(() => {
    if (currentStep === 'initTransfer') {
      setIsOpen(prev => !prev);
      return;
    }
    onRefetchTransactionDetails();
    setIsOpen(prev => !prev);
  }, [currentStep, onRefetchTransactionDetails]);

  const onNavigateBackToDashboard = useCallback(() => {
    onRefetchTransactionDetails();
    if (selectedCustomerId) {
      navigate(
        getRoutePath({
          routeKey: 'dashboard',
          customerId: selectedCustomerId as string,
        }),
      );
    }
  }, [selectedCustomerId, navigate, onRefetchTransactionDetails]);

  const classes = useStyles();

  return (
    <>
      <Button
        variant='contained'
        color='primary'
        title={t('authorizationsListPage.TransactionInvolvedPartiesWithDealerRate.Finalize.Text')}
        onClick={onToggleDealerRateAcceptanceDialog}>
        {t('authorizationsListPage.TransactionInvolvedPartiesWithDealerRate.Finalize.Text')}
      </Button>
      <GenericDialog
        fullwidth
        isError={false}
        isLoading={false}
        isConfirmationResponsiveDialog={true}
        isDialogOpen={isOpen}
        dialogContentClassName={classes.dialogContentClassName}
        onToggleDialog={onToggleDealerRateAcceptanceDialog}
        stickDrawerTitleToLeft={true}
        disableMobileDrawerHeight={true}
        dialogTitle={''}
        loaderVerticalSpacing='large'
        dialogContent={
          <ClientSideWizardFlowPresenter
            processName={'fundsTransfer'}
            preloadedFormDataConfig={{
              data: {
                initiateAuth: {
                  stepId: 'initiateAuth',
                  values: {
                    transactionDetails,
                    fundTransfersService,
                    onRefetchTransactionDetails,
                    onCancel: onToggleDealerRateAcceptanceDialog,
                    onBackHome: onNavigateBackToDashboard,
                    setCurrentStep,
                  },
                },
                END: {
                  stepId: 'END',
                  values: {
                    transactionDetails,
                    fundTransfersService,
                    onRefetchTransactionDetails,
                    onCancel: onToggleDealerRateAcceptanceDialog,
                    onBackHome: onNavigateBackToDashboard,
                    setCurrentStep,
                  },
                },
              },
              currentStep: currentStep === 'initTransfer' ? 'initTransfer' : 'makeTransfer',
            }}
            stepsConfig={{
              initiateAuth: {
                stepId: 'initiateAuth',
                isFirstStep: currentStep === 'initTransfer',
                cancelHidden: false,
                hasStepIndicator: false,
                Component: InitiateRequestDealerInApp2FA,
                parameters: {},
              },
              END: {
                stepId: 'END',
                isFirstStep: currentStep !== 'initTransfer',
                cancelHidden: false,
                hasStepIndicator: false,
                Component: SuccessAcceptedDealerRate,
                parameters: {},
              },
            }}
            isTakeover={false}
            shouldEnablePaddingBottom={false}
          />
        }
        isSuccess={false}
      />
    </>
  );
};

export { Finalize };
